import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { LocaleProvider } from '@/lib/i18n';
import { CurrencyProvider } from '@/lib/currency';
import { AuthProvider } from '@/lib/auth/AuthContext';
import { ThemeProvider } from '@/lib/theme-context';
import { HomePage } from '@/features/home';
import { SignIn, SignUp, ForgotPassword, ResetPassword } from '@/features/auth';
import { SettingsLayout, ProfileSettings, SecuritySettings, LinkedAccounts, NotificationSettings } from '@/features/settings';
import { SupportPage } from '@/features/support';
import { GiveawaysPage } from '@/features/giveaways';
import { DashboardPage, WalletPage, OrdersPage } from '@/features/dashboard';
import { RequireAuth } from '@/components/RequireAuth';

import { WinterOverlay } from '@/features/home/Header/WinterOverlay';
import '@/styles/tokens.css';
import '@/styles/global.css';

const MockCheckoutPage = lazy(() => import('@/features/payments').then((m) => ({ default: m.MockCheckoutPage })));
const BecomeSellerPage = lazy(() => import('@/features/seller').then((m) => ({ default: m.BecomeSellerPage })));
const SellerLanding = lazy(() => import('@/features/seller').then((m) => ({ default: m.SellerLanding })));
const CreateListing = lazy(() => import('@/features/seller').then((m) => ({ default: m.CreateListing })));
const SellerListingsPage = lazy(() => import('@/features/seller').then((m) => ({ default: m.SellerListingsPage })));
const CategoryBrowsePage = lazy(() => import('@/features/marketplace').then((m) => ({ default: m.CategoryBrowsePage })));
const ProductDetailPage = lazy(() => import('@/features/marketplace').then((m) => ({ default: m.ProductDetailPage })));
const CheckoutPage = lazy(() => import('@/features/marketplace').then((m) => ({ default: m.CheckoutPage })));
const BrowsePage = lazy(() => import('@/features/browse').then((m) => ({ default: m.BrowsePage })));
const ChatPage = lazy(() => import('@/features/chat').then((m) => ({ default: m.ChatPage })));
const SellerProfilePage = lazy(() => import('@/features/seller-profile').then((m) => ({ default: m.SellerProfilePage })));
const SellerDashboardPage = lazy(() => import('@/features/seller-dashboard').then((m) => ({ default: m.SellerDashboardPage })));
const AdminAuthPage = lazy(() => import('@/features/admin').then((m) => ({ default: m.AdminAuthPage })));
const AdminLayout = lazy(() => import('@/features/admin').then((m) => ({ default: m.AdminLayout })));
const CatalogManager = lazy(() => import('@/features/admin').then((m) => ({ default: m.CatalogManager })));
const HeroManager = lazy(() => import('@/features/admin').then((m) => ({ default: m.HeroManager })));
const FinancialDashboard = lazy(() => import('@/features/admin').then((m) => ({ default: m.FinancialDashboard })));
const SellerAgreementManager = lazy(() => import('@/features/admin').then((m) => ({ default: m.SellerAgreementManager })));
const MarketplaceSettingsManager = lazy(() => import('@/features/admin').then((m) => ({ default: m.MarketplaceSettingsManager })));
const SellerManager = lazy(() => import('@/features/admin/sellers').then((m) => ({ default: m.SellerManager })));
const SellerDetail = lazy(() => import('@/features/admin/sellers').then((m) => ({ default: m.SellerDetail })));
const SellerReportsManager = lazy(() => import('@/features/admin/sellers').then((m) => ({ default: m.SellerReportsManager })));
const RankConfigManager = lazy(() => import('@/features/admin/sellers').then((m) => ({ default: m.RankConfigManager })));
const BadgeConfigManager = lazy(() => import('@/features/admin/sellers').then((m) => ({ default: m.BadgeConfigManager })));
const WeightConfigManager = lazy(() => import('@/features/admin/sellers').then((m) => ({ default: m.WeightConfigManager })));
const UserManager = lazy(() => import('@/features/admin/users').then((m) => ({ default: m.UserManager })));
const UserDetail = lazy(() => import('@/features/admin/users').then((m) => ({ default: m.UserDetail })));
const AuditLogPage = lazy(() => import('@/features/admin/logs').then((m) => ({ default: m.AuditLogPage })));
const SupportTicketsPage = lazy(() => import('@/features/admin/support').then((m) => ({ default: m.SupportTicketsPage })));

function PageLoader() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-surface-base">
      <Loader2 size={28} className="animate-spin text-accent" />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
    <WinterOverlay />
    <LocaleProvider>
      <CurrencyProvider>
      <AuthProvider>
        <MotionConfig reducedMotion="user">
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/auth/signin" element={<SignIn />} />
                <Route path="/auth/signup" element={<SignUp />} />
                <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/giveaways" element={<GiveawaysPage />} />
                <Route path="/giveaways-terms" element={<GiveawaysPage />} />
                <Route path="/giveaways/terms" element={<Navigate to="/giveaways-terms" replace />} />
                <Route element={<RequireAuth />}>
                  <Route path="/dashboard" element={<DashboardPage />}>
                    <Route index element={<Navigate to="/dashboard/wallet" replace />} />
                    <Route path="wallet" element={<WalletPage />} />
                    <Route path="orders" element={<OrdersPage />} />
                    <Route path="listings" element={<SellerListingsPage />} />
                    <Route path="messages" element={<ChatPage />} />
                  </Route>
                </Route>
                <Route path="/auth/login" element={<Navigate to="/auth/signin" replace />} />
                <Route path="/auth/register" element={<Navigate to="/auth/signup" replace />} />
                <Route path="/settings" element={<SettingsLayout />}>
                  <Route index element={<Navigate to="/settings/profile" replace />} />
                  <Route path="profile" element={<ProfileSettings />} />
                  <Route path="security" element={<SecuritySettings />} />
                  <Route path="linked-accounts" element={<LinkedAccounts />} />
                  <Route path="notifications" element={<NotificationSettings />} />
                </Route>
                <Route path="/payments/mock/:invoiceId" element={<MockCheckoutPage />} />
                <Route path="/admin/auth" element={<AdminAuthPage />} />
                <Route element={<RequireAuth />}>
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<FinancialDashboard />} />
                    <Route path="catalog" element={<CatalogManager />} />
                    <Route path="hero" element={<HeroManager />} />
                    <Route path="seller-agreement" element={<SellerAgreementManager />} />
                    <Route path="settings" element={<MarketplaceSettingsManager />} />
                    <Route path="sellers" element={<SellerManager />} />
                    <Route path="sellers/:userId" element={<SellerDetail />} />
                    <Route path="seller-reports" element={<SellerReportsManager />} />
                    <Route path="rank-config" element={<RankConfigManager />} />
                    <Route path="badge-config" element={<BadgeConfigManager />} />
                    <Route path="weights" element={<WeightConfigManager />} />
                    <Route path="users" element={<UserManager />} />
                    <Route path="users/:userId" element={<UserDetail />} />
                    <Route path="logs" element={<AuditLogPage />} />
                    <Route path="support-tickets" element={<SupportTicketsPage />} />
                  </Route>
                </Route>
                <Route path="/seller" element={<SellerLanding />} />
                <Route path="/seller/activate" element={<BecomeSellerPage />} />
                <Route path="/seller/listings/new" element={<CreateListing />} />
                <Route element={<RequireAuth />}>
                  <Route path="/seller/dashboard" element={<SellerDashboardPage />} />
                </Route>
                <Route path="/seller/:username" element={<SellerProfilePage />} />
                <Route path="/category/:slug" element={<CategoryBrowsePage />} />
                <Route path="/product/:id" element={<ProductDetailPage />} />
                <Route path="/checkout/:offerId" element={<CheckoutPage />} />
                <Route path="/browse" element={<BrowsePage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </MotionConfig>
      </AuthProvider>
      </CurrencyProvider>
    </LocaleProvider>
    </ThemeProvider>
  );
}

export default App;